import { Button, Card, Col, Descriptions, Empty, Popconfirm, Row, Space, Spin, Tag, Timeline, message } from 'antd';
import {
  CalendarOutlined,
  DeleteOutlined,
  EditOutlined,
  ProjectOutlined,
  ShopOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { OwnerType } from '@never2busy/shared';
import { accountsApi } from '../../../api/endpoints/accounts';
import { personsApi } from '../../../api/endpoints/persons';
import { activitiesApi } from '../../../api/endpoints/activities';
import { projectsApi } from '../../../api/endpoints/projects';
import { salesApi } from '../../../api/endpoints/sales';
import { PageHeader } from '../../../components/common/PageHeader';
import { FavoriteToggle } from '../../../components/common/FavoriteToggle';
import { KeywordsManager } from '../../../components/common/KeywordsManager';
import { AccountPermissionsCard } from '../components/AccountPermissionsCard';

interface ActivityLite {
  id: number;
  type: string | null;
  notes: string;
  startDate: string | null;
}
interface SaleLite {
  id: number;
  name: string;
  amount: string | null;
  saleDate: string | null;
  createdAt: string;
  currency?: { code: string } | null;
}

/** Línea de tiempo de la cuenta: actividades + ventas, más recientes primero. */
function buildTimeline(activities: ActivityLite[], sales: SaleLite[]) {
  const entries = [
    ...activities.map((a) => {
      const d = a.startDate ? dayjs(a.startDate) : null;
      return {
        ts: d ? d.valueOf() : 0,
        color: 'blue',
        children: (
          <>
            <Link to={`/actividades/${a.id}`}>
              {a.type ?? 'Actividad'}
              {a.notes ? ` · ${a.notes.slice(0, 50)}` : ''}
            </Link>
            <div style={{ fontSize: 12, color: '#888' }}>
              {d ? d.format('DD/MM/YYYY HH:mm') : 'Sin fecha'}
            </div>
          </>
        ),
      };
    }),
    ...sales.map((s) => {
      const d = s.saleDate ? dayjs(s.saleDate) : dayjs(s.createdAt);
      return {
        ts: d.valueOf(),
        color: 'green',
        children: (
          <>
            <Link to={`/ventas/${s.id}`}>Venta · {s.name}</Link>
            <div style={{ fontSize: 12, color: '#888' }}>
              {d.format('DD/MM/YYYY')}
              {s.amount
                ? ` · ${parseFloat(s.amount).toLocaleString('es-MX')} ${s.currency?.code ?? ''}`
                : ''}
            </div>
          </>
        ),
      };
    }),
  ];
  return entries
    .sort((a, b) => b.ts - a.ts)
    .slice(0, 12)
    .map(({ color, children }) => ({ color, children }));
}

export function AccountDetailPage(): JSX.Element {
  const { id } = useParams();
  const accountId = id ? parseInt(id, 10) : 0;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['account', accountId],
    queryFn: () => accountsApi.findOne(accountId),
  });

  const personsQuery = useQuery({
    queryKey: ['persons', { accountId }],
    queryFn: () => personsApi.list({ accountId, page: 1, pageSize: 50 }),
    enabled: !!accountId,
  });

  const activitiesQuery = useQuery({
    queryKey: ['activities', { accountId }],
    queryFn: () => activitiesApi.list({ accountId, page: 1, pageSize: 20 }),
    enabled: !!accountId,
  });

  const projectsQuery = useQuery({
    queryKey: ['projects', { customerId: accountId }],
    queryFn: () => projectsApi.list({ customerId: accountId, page: 1, pageSize: 20 }),
    enabled: !!accountId,
  });

  const salesQuery = useQuery({
    queryKey: ['sales', { accountId }],
    queryFn: () => salesApi.list({ accountId, page: 1, pageSize: 20 }),
    enabled: !!accountId,
  });

  const removeMutation = useMutation({
    mutationFn: () => accountsApi.remove(accountId),
    onSuccess: () => {
      message.success('Cuenta eliminada');
      void queryClient.invalidateQueries({ queryKey: ['accounts'] });
      navigate('/cuentas');
    },
    onError: () => message.error('No se pudo eliminar la cuenta'),
  });

  if (isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  if (!data) {
    return <div>Cuenta no encontrada</div>;
  }

  const timeline = buildTimeline(
    activitiesQuery.data?.data ?? [],
    salesQuery.data?.data ?? [],
  );

  return (
    <>
      <PageHeader
        title={
          <Space>
            <span>{data.name}</span>
            <FavoriteToggle ownerType={OwnerType.ACCOUNT} ownerId={data.id} />
          </Space>
        }
        crumbs={[{ title: 'Cuentas', to: '/cuentas' }, { title: data.name }]}
        extra={
          <Space wrap>
            <Button
              icon={<UserAddOutlined />}
              onClick={() => navigate(`/contactos/nuevo?accountId=${data.id}`)}
            >
              + Contacto
            </Button>
            <Button
              icon={<ProjectOutlined />}
              onClick={() => navigate(`/proyectos/nuevo?customerId=${data.id}`)}
            >
              + Proyecto
            </Button>
            <Button
              icon={<ShopOutlined />}
              onClick={() => navigate(`/ventas/nueva?accountId=${data.id}`)}
            >
              + Venta
            </Button>
            <Button
              icon={<CalendarOutlined />}
              onClick={() => navigate(`/actividades/nueva?accountId=${data.id}`)}
            >
              + Actividad
            </Button>
            <Button
              icon={<EditOutlined />}
              onClick={() => navigate(`/cuentas/${data.id}/editar`)}
            >
              Editar
            </Button>
            <Popconfirm
              title="¿Eliminar esta cuenta?"
              description="Se marca como eliminada (soft-delete)."
              okText="Sí, eliminar"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
              onConfirm={() => removeMutation.mutate()}
            >
              <Button danger icon={<DeleteOutlined />} loading={removeMutation.isPending}>
                Eliminar
              </Button>
            </Popconfirm>
          </Space>
        }
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title="Datos generales">
            <Descriptions column={{ xs: 1, sm: 2 }} size="small">
              <Descriptions.Item label="Nombre corto">{data.shortName ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="RFC / Tax ID">{data.taxId ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Contacto principal">
                {data.primaryPerson ? (
                  <Link to={`/contactos/${data.primaryPerson.id}`}>
                    {data.primaryPerson.firstName ?? ''} {data.primaryPerson.lastName}
                  </Link>
                ) : (
                  '—'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Giro">{data.businessLine ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Categoría">
                {data.category ? <Tag>{data.category}</Tag> : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Correo">{data.email ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Teléfono">{data.phone ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Sitio web">{data.website ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Dirección">
                {data.addressLine1
                  ? `${data.addressLine1}, ${data.city ?? ''} ${data.state ?? ''}`.trim()
                  : '—'}
              </Descriptions.Item>
            </Descriptions>
            {data.notes && (
              <div style={{ marginTop: 16 }}>
                <strong>Notas:</strong>
                <div style={{ whiteSpace: 'pre-wrap' }}>{data.notes}</div>
              </div>
            )}
            <div style={{ marginTop: 16 }}>
              <strong>Etiquetas:</strong>
              <div style={{ marginTop: 8 }}>
                <KeywordsManager ownerType={OwnerType.ACCOUNT} ownerId={data.id} />
              </div>
            </div>
          </Card>

          <Card title="Actividad reciente" style={{ marginTop: 16 }}>
            {timeline.length > 0 ? (
              <Timeline items={timeline} />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Sin actividades ni ventas registradas"
              />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <AccountPermissionsCard accountId={data.id} />

          <Card title="Contactos" style={{ marginBottom: 16 }}>
            {personsQuery.data?.data.length ? (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {personsQuery.data.data.map((p) => (
                  <li key={p.id} style={{ padding: '4px 0' }}>
                    <Link to={`/contactos/${p.id}`}>
                      {p.firstName ?? ''} {p.lastName}
                    </Link>
                    {p.jobTitle && (
                      <div style={{ color: '#888', fontSize: 12 }}>{p.jobTitle}</div>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <div style={{ color: '#888' }}>Sin contactos asociados</div>
            )}
          </Card>

          <Card title="Proyectos" style={{ marginBottom: 16 }}>
            {projectsQuery.data?.data.length ? (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {projectsQuery.data.data.map((p) => (
                  <li key={p.id} style={{ padding: '4px 0' }}>
                    <Link to={`/proyectos/${p.id}`}>
                      {p.projectNumber ? `${p.projectNumber} · ` : ''}
                      {p.name}
                    </Link>
                    {p.status && <Tag style={{ marginLeft: 8 }}>{p.status}</Tag>}
                  </li>
                ))}
              </ul>
            ) : (
              <div style={{ color: '#888' }}>Sin proyectos asociados</div>
            )}
          </Card>

          <Card title="Ventas" style={{ marginBottom: 16 }}>
            {salesQuery.data?.data.length ? (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {salesQuery.data.data.map((s) => (
                  <li key={s.id} style={{ padding: '4px 0' }}>
                    <Link to={`/ventas/${s.id}`}>
                      {s.saleNumber ? `${s.saleNumber} · ` : ''}
                      {s.name}
                    </Link>
                    {s.amount && (
                      <Tag style={{ marginLeft: 8 }} color="blue">
                        {parseFloat(s.amount).toLocaleString('es-MX')}{' '}
                        {s.currency?.code ?? ''}
                      </Tag>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <div style={{ color: '#888' }}>Sin ventas asociadas</div>
            )}
          </Card>

          <Card title="Actividades recientes">
            {activitiesQuery.data?.data.length ? (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {activitiesQuery.data.data.map((a) => (
                  <li key={a.id} style={{ padding: '4px 0' }}>
                    <Link to={`/actividades/${a.id}`}>
                      {a.type ?? 'Actividad'} —{' '}
                      {a.notes?.slice(0, 60)}
                      {a.notes && a.notes.length > 60 ? '…' : ''}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <div style={{ color: '#888' }}>Sin actividades</div>
            )}
          </Card>
        </Col>
      </Row>
    </>
  );
}
